/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useText } from '@adobe-commerce/elsie/i18n';
import { useFormProps } from '@/account/types';
import {
  validationFields,
  ValidationFieldsConfig,
} from '@/account/lib/validationFields';
import normalizeGetAddressData from '@/account/lib/normalizeGetAddressData';
import getFormValuesFromRef from '@/account/lib/getFormValuesFromRef';
import areRequiredFieldsValid from '@/account/lib/areRequiredFieldsValid';
import { checkIsFunction } from '@/account/lib/checkIsFunction';
import { CustomerAddressesModel } from '@/account/data/models';
import initializeFormDataAndErrors from '@/account/lib/initializeFormDataAndErrors';

const isObjectNotEmpty = (obj: Record<string, string>) =>
  Object.keys(obj).length > 0;

export const useForm = ({
  fieldsConfig,
  onSubmit,
  onChange,
  setInputChange,
  formName,
}: useFormProps) => {
  const translations = useText({
    requiredFieldError: 'Account.FormText.requiredFieldError',
    lengthTextError: 'Account.FormText.lengthTextError',
    numericError: 'Account.FormText.numericError',
    alphaNumWithSpacesError: 'Account.FormText.alphaNumWithSpacesError',
    alphaNumericError: 'Account.FormText.alphaNumericError',
    alphaError: 'Account.FormText.alphaError',
    emailError: 'Account.FormText.emailError',
    dateError: 'Account.FormText.dateError',
    dateLengthError: 'Account.FormText.dateLengthError',
    urlError: 'Account.FormText.urlError',
  });

  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<Record<string, string | number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requiredFields, setRequiredFields] = useState({});
  const [regionEnabled, setRegionEnabled] = useState(true);
  const [effectHasRun, setEffectHasRun] = useState(false);
  const [shouldBlockEffect, setShouldBlockEffect] = useState(false);
  const [shouldTriggerEffect, setShouldTriggerEffect] = useState(true);
  const [isDataValid, setIsDataValid] = useState(false);

  useEffect(() => {
    const getGridRowCount = () => {
      if (formRef.current) {
        const computedStyles = window.getComputedStyle(formRef.current);
        const rows = computedStyles
          .getPropertyValue('grid-template-rows')
          .split(' ').length;
        const checkbox = formRef.current.querySelector(
          '.account-address-form--saveAddressBook'
        );

        if (checkbox) {
          (checkbox as HTMLInputElement).style.gridRow = String(rows - 1);
        }
      }
    };

    getGridRowCount();

    window.addEventListener('resize', getGridRowCount);

    return () => {
      window.removeEventListener('resize', getGridRowCount);
    };
  }, [fieldsConfig?.length]);

  const handleValidationSubmit = useCallback(
    (disableShowError = false) => {
      let formValid = true;
      const errorsList = { ...errors };
      let firstErrorField: string | null = null;

      for (const [name, value] of Object.entries(formData)) {
        const fieldConfig = fieldsConfig?.find((config) =>
          config.customUpperCode.includes(name)
        );

        const validationResult = validationFields(
          value.toString(),
          fieldConfig as ValidationFieldsConfig,
          translations,
          errorsList
        );

        if (validationResult[name]) {
          Object.assign(errorsList, validationResult);
          formValid = false;
        }

        if (!firstErrorField) {
          firstErrorField =
            Object.keys(errorsList).find((key) => errorsList[key]) || null;
        }
      }

      if (!disableShowError) {
        setErrors(errorsList);
      }

      if (firstErrorField && formRef.current && !disableShowError) {
        const input = formRef.current.elements.namedItem(
          firstErrorField
        ) as HTMLElement;
        input?.focus();
      }

      return formValid;
    },
    [errors, fieldsConfig, formData, translations]
  );

  const collectFormData = useCallback(
    (
      fieldValue: string | number | boolean,
      name: string,
      requiredList: Record<string, string>,
      event: Event | null
    ) => {
      const fieldsForm = {
        ...getFormValuesFromRef(formRef),
        [name]: fieldValue,
        ...(name.includes('countryCode') ? { region: '' } : {}),
      };

      const data = {
        data: normalizeGetAddressData(fieldsForm, true),
        isDataValid: areRequiredFieldsValid(requiredList, fieldsForm),
      };
      setIsDataValid(data.isDataValid);
      handleValidationSubmit(true);

      ['selectedShippingAddress', 'selectedBillingAddress'].includes(
        formName
      ) &&
        sessionStorage.setItem(`${formName}_addressData`, JSON.stringify(data));
      // @ts-ignore
      onChange?.(data, {}, event);
    },
    [handleValidationSubmit, formName, onChange]
  );

  useEffect(() => {
    if (fieldsConfig?.length) {
      const { initialData, errorList } =
        initializeFormDataAndErrors(fieldsConfig);

      setFormData((prev) => ({
        ...(initialData as Record<string, string | number>),
        ...prev,
      }));

      setErrors(errorList);
      setRequiredFields(errorList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fieldsConfig)]);

  useEffect(() => {
    if (shouldBlockEffect) return;
    // This code is responsible for initializing data when we retrieve it from session storage. It validates the data, sets the initial configurations for the form, and sends the data through the callback to check if it's valid or not.
    const fields = getFormValuesFromRef(formRef);
    const sessionAddressForm = sessionStorage.getItem(
      `${formName}_addressData`
    );

    if (
      isObjectNotEmpty(formData as Record<string, string>) &&
      isObjectNotEmpty(requiredFields)
    ) {
      let data = {};

      const isDataValid = areRequiredFieldsValid(
        requiredFields,
        formData as Record<string, string | number | boolean>
      );

      if (sessionAddressForm) {
        data = JSON.parse(sessionAddressForm).data as CustomerAddressesModel;
      } else {
        // This code is used as an initializer and needs to run once during the page load in the default state, as the user can pass default parameters via props.
        data = normalizeGetAddressData(fields, true) ?? {};
      }

      // @ts-ignore
      onChange?.({ data, isDataValid }, {}, null);
      setIsDataValid(isDataValid);
      setShouldBlockEffect(true);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [formData, requiredFields]);

  useEffect(() => {
    if (!shouldTriggerEffect) return;

    const fields = getFormValuesFromRef(formRef);
    const hasCountryCode = Boolean(fields?.countryCode);
    const hasRegion = Boolean(fields?.region?.length);
    const shouldTriggerChange =
      fields && hasCountryCode && !hasRegion && checkIsFunction(onChange);

    if (shouldTriggerChange) {
      collectFormData(fields?.region, 'region', requiredFields, null);
    }
  }, [
    shouldTriggerEffect,
    regionEnabled,
    fieldsConfig,
    formRef,
    onChange,
    collectFormData,
    requiredFields,
    effectHasRun,
  ]);

  const handleInputChange = useCallback(
    (event: Event, errorsProp?: Record<string, string>) => {
      const { name, value, type, checked } = event?.target as HTMLInputElement;
      const fieldValue = type === 'checkbox' ? checked : value;

      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          [name]: fieldValue,
        };

        if (name === 'countryCode') {
          updatedFormData.region = '';
          setRegionEnabled(true);
          setEffectHasRun(false);
        }

        return updatedFormData as Record<string, string | number>;
      });

      setInputChange?.({ [name]: fieldValue });
      setShouldBlockEffect(true);

      const fieldConfig = fieldsConfig?.find((config) =>
        config.customUpperCode.includes(name)
      );

      let errorsList = errorsProp ? { ...errorsProp } : { ...errors };

      if (fieldConfig) {
        const validationResult = validationFields(
          fieldValue.toString(),
          fieldConfig as ValidationFieldsConfig,
          translations,
          errorsList
        );

        if (validationResult) {
          Object.assign(errorsList, validationResult);
        }

        setErrors(errorsList);
      }

      collectFormData(fieldValue, name, requiredFields, event);
    },
    [
      setInputChange,
      fieldsConfig,
      errors,
      translations,
      collectFormData,
      requiredFields,
      regionEnabled,
    ]
  );

  const onFocus = useCallback((event: Event) => {
    const { name } = event?.target as HTMLInputElement;

    const fieldConfig = fieldsConfig?.find(
      (config) => config.customUpperCode === name
    );

    if (name === 'region' && fieldConfig?.options.length) {
      setShouldTriggerEffect(false);
    }

    if (name !== 'countryCode') {
      setShouldTriggerEffect(false);
    } else {
      setShouldTriggerEffect(true);
    }
  }, []);

  const handleBlur = useCallback(
    (event: Event, errorsProp?: Record<string, string>) => {
      const { name, value, type, checked } = event?.target as HTMLInputElement;
      const fieldValue = type === 'checkbox' ? checked : value;

      const fieldConfig = fieldsConfig?.find(
        (config) => config.customUpperCode === name
      );

      if (fieldConfig) {
        const errorsList = errorsProp ? { ...errorsProp } : { ...errors };

        const validationResult = validationFields(
          fieldValue.toString(),
          fieldConfig as ValidationFieldsConfig,
          translations,
          errorsList
        );

        if (validationResult) {
          Object.assign(errorsList, validationResult);
        }

        setErrors(errorsList);
      }
    },
    [errors, fieldsConfig, translations]
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent) => {
      event.preventDefault();

      const formValid = handleValidationSubmit();

      onSubmit?.(event, formValid);
    },
    [handleValidationSubmit, onSubmit]
  );

  return {
    isDataValid,
    formData,
    errors,
    formRef,
    handleInputChange,
    onFocus,
    handleBlur,
    handleSubmit,
    handleValidationSubmit,
  };
};
